import { DaoType, DefaultDtoMapper, DefaultSyncDtoMapper, DtoType } from '../mapper';
import { PagedData } from '../paged-data';
import { PathDao } from './path.dao';
import { PathDto } from './path.dto';
import { PathSegmentDao } from './path-segment.dao';
import { PathSegmentDto } from './path-segment.dto';
import { SearchResultDao, withoutResultsMissingResource } from './search-result.dao';
import { SearchResultDto } from './search-result.dto';

export abstract class SearchResultMapper<Dao extends DaoType, Dto extends DtoType> extends DefaultDtoMapper<Dao, Dto> {
  protected override _sync: SearchResultSyncMapper<Dao, Dto>;

  public override get sync(): SearchResultSyncMapper<Dao, Dto> {
    if (!this._sync) {
      this._sync = this.createSyncMapper();
    }
    return this._sync;
  }

  public override createSyncMapper(): SearchResultSyncMapper<Dao, Dto> {
    return new SearchResultSyncMapper(this.getDtoSample(), this.getDaoSample());
  }

  public fromSearchResultDao(pagedData: Promise<PagedData<SearchResultDao<Partial<Dao>>>>): Promise<PagedData<SearchResultDto<Dto>>> {
    return pagedData.then((awaitedPagedData) => {
      return this.sync.fromSearchResultDao(awaitedPagedData);
    });
  }
}

export class SearchResultSyncMapper<Dao extends DaoType, Dto extends DtoType> extends DefaultSyncDtoMapper<Dao, Dto> {
  public fromSearchResultDao(rawPagedData: PagedData<SearchResultDao<Partial<Dao>>>): PagedData<SearchResultDto<Dto>> {
    const pagedData = withoutResultsMissingResource(rawPagedData);
    //TODO duplicates fromPagedDao
    return {
      content: pagedData?.content?.map((dao) => this.mapSearchResult(dao)),
      totalPages: pagedData?.totalPages,
      totalElements: pagedData?.totalElements,
      number: pagedData?.number,
      size: pagedData?.size,
      first: pagedData?.first,
      last: pagedData?.last,
    };
  }

  private mapSearchResult(dao: SearchResultDao<Partial<Dao>>): SearchResultDto<Dto> {
    return {
      resourceId: dao.resourceId,
      resourceType: dao.resourceType,
      // A partial resource is enough: fields it lacks are filled from the DTO sample.
      resource: this.fromDaoUnsafe(withoutNullFields(dao.resource) as Dao),
      paths: this.mapPaths(dao.paths),
    };
  }

  private mapPaths(pathDaos?: PathDao[]): PathDto[] {
    if (pathDaos) {
      return pathDaos.map((dao) => {
        return {
          uri: dao.uri,
          segments: this.mapSegments(dao.segments),
        };
      });
    } else {
      return [];
    }
  }

  private mapSegments(segmentDaos?: PathSegmentDao[]): PathSegmentDto[] {
    // Nameless segments stay: navigation resolves tenant/organisation/team ids from them by type.
    return (segmentDaos ?? []).map((dao) => {
      return {
        id: dao.id,
        name: dao.name,
        type: dao.type,
      };
    });
  }
}

/**
 * The sample only fills keys that are absent, so a field the backend sent as `null` or `undefined`
 * would reach the DTO as is. Dropping those keys lets the sample default apply to them too.
 */
function withoutNullFields<T extends object>(resource: T | undefined): T {
  return Object.fromEntries(Object.entries(resource ?? {}).filter(([, value]) => value != null)) as T;
}
